import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatMoney, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import {
  Phone, MessageSquare, Sparkles, TrendingUp, AlertTriangle,
  CalendarClock, PackageCheck, RefreshCcw, Search, ChevronRight,
} from "lucide-react";

const WINDOWS = [
  { v: 1, label: "Next 1 day" },
  { v: 2, label: "Next 2 days" },
  { v: 3, label: "Next 3 days" },
  { v: 7, label: "Next 7 days" },
];

function makeWhatsappLink(row) {
  if (!row.phone) return null;
  const items = (row.last_items || []).map((i) => `${i.brand} ${i.size}× ${i.filled_qty}`).join(", ");
  const txt = `Hello ${row.party_name}, this is a courtesy reminder — based on your past orders we expect you may need a refill around ${row.predicted_next_date}. Last order was ${items || "noted on " + row.last_order_date}. Should we plan a delivery?`;
  return `https://wa.me/${row.phone.replace(/\D/g, "")}?text=${encodeURIComponent(txt)}`;
}

export default function OrderPredictions() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [daysAhead, setDaysAhead] = useState(2);
  const [includeOverdue, setIncludeOverdue] = useState(true);
  const [minOrders, setMinOrders] = useState(2);
  const [search, setSearch] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/predictions/orders", {
        params: { days_ahead: daysAhead, min_orders: minOrders, include_overdue: includeOverdue },
      });
      setData(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [daysAhead, minOrders, includeOverdue]);

  const rows = useMemo(() => {
    const r = data?.rows || [];
    const q = search.trim().toLowerCase();
    return q ? r.filter((x) => x.party_name.toLowerCase().includes(q)) : r;
  }, [data, search]);

  const tagFor = (r) => {
    if (r.days_until_predicted < 0) return { cls: "bg-[#FEE2E2] text-[#B91C1C]", txt: `${Math.abs(r.days_until_predicted)}d overdue` };
    if (r.days_until_predicted === 0) return { cls: "bg-[#FEF3C7] text-[#B45309]", txt: "Due today" };
    if (r.days_until_predicted === 1) return { cls: "bg-[#FED7AA] text-[#9A3412]", txt: "Tomorrow" };
    return { cls: "bg-[#DBEAFE] text-[#1D4ED8]", txt: `in ${r.days_until_predicted}d` };
  };

  const logFollowup = async (row) => {
    const c = window.prompt(`Quick note for ${row.party_name} (will be added to follow-up log):`);
    if (c == null) return;
    try {
      await api.post(`/followups/${row.party_id}/log`, {
        comment: c || `Order reminder · predicted ${row.predicted_next_date}`,
        next_followup_date: row.predicted_next_date,
      });
      toast.success("Logged in follow-ups");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="order-predictions-page">
      {/* Heading */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Sparkles size={20} className="text-[#B45309]" />
          <h2 className="font-heading font-black text-2xl tracking-tight">Order Predictions</h2>
        </div>
        <span className="text-xs text-gray-500 border border-gray-200 rounded-sm px-2 py-0.5">
          Based on each customer's average gap between orders · today {data?.today || ""}
        </span>
        <button onClick={load} className="ml-auto h-9 px-3 border border-gray-300 rounded-sm text-xs font-semibold inline-flex items-center gap-1.5 hover:border-black" data-testid="prediction-refresh">
          <RefreshCcw size={13} /> Refresh
        </button>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="card-flat p-5">
          <div className="small-label text-gray-500 flex items-center gap-1.5"><PackageCheck size={12} /> Expected orders</div>
          <div className="data-num text-3xl mt-1" data-testid="prediction-count">{data?.summary?.count || 0}</div>
          <div className="text-xs text-gray-500 mt-1">within next {daysAhead}d</div>
        </div>
        <div className="card-flat p-5 bg-[#FEE2E2] border-[#FECACA]">
          <div className="small-label text-[#B91C1C] flex items-center gap-1.5"><AlertTriangle size={12} /> Overdue</div>
          <div className="data-num text-3xl mt-1 text-[#B91C1C]" data-testid="prediction-overdue">{data?.summary?.overdue_count || 0}</div>
          <div className="text-xs text-[#B91C1C]/80 mt-1">Order should have arrived</div>
        </div>
        <div className="card-flat p-5 bg-[#FEF3C7] border-[#FDE68A]">
          <div className="small-label text-[#B45309] flex items-center gap-1.5"><CalendarClock size={12} /> Due today</div>
          <div className="data-num text-3xl mt-1 text-[#B45309]" data-testid="prediction-today">{data?.summary?.due_today_count || 0}</div>
          <div className="text-xs text-[#B45309]/80 mt-1">Reach out before evening</div>
        </div>
        <div className="card-flat p-5 bg-black text-white border-black">
          <div className="small-label text-white/70 flex items-center gap-1.5"><TrendingUp size={12} /> Expected revenue</div>
          <div className="data-num text-3xl mt-1" data-testid="prediction-revenue">₹ {formatMoney(data?.summary?.expected_revenue || 0)}</div>
          <div className="text-xs text-white/70 mt-1">~{data?.summary?.expected_filled_qty || 0} cyl based on avg order</div>
        </div>
      </div>

      {/* Controls */}
      <div className="card-flat p-4 flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          {WINDOWS.map((w) => (
            <button key={w.v} onClick={() => setDaysAhead(w.v)}
              className={`px-3 h-9 rounded-sm text-xs font-semibold border ${daysAhead === w.v ? "bg-black text-white border-black" : "bg-white text-gray-600 border-gray-300 hover:border-black"}`}
              data-testid={`window-${w.v}`}>
              {w.label}
            </button>
          ))}
        </div>
        <label className="text-xs text-gray-600 flex items-center gap-2 ml-2">
          <input type="checkbox" checked={includeOverdue} onChange={(e) => setIncludeOverdue(e.target.checked)} data-testid="include-overdue" />
          Include overdue
        </label>
        <div className="text-xs text-gray-600 flex items-center gap-2">
          Min orders to predict
          <select className="input-lg h-9 w-20" value={minOrders} onChange={(e) => setMinOrders(Number(e.target.value))} data-testid="min-orders">
            <option value={2}>2</option>
            <option value={3}>3</option>
            <option value={5}>5</option>
          </select>
        </div>
        <div className="ml-auto relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input className="input-lg h-9 w-48 pl-8" placeholder="Search party…" value={search}
            onChange={(e) => setSearch(e.target.value)} data-testid="prediction-search" />
        </div>
      </div>

      {/* Table */}
      <div className="card-flat overflow-auto">
        <table className="table-dense w-full">
          <thead>
            <tr>
              <th className="text-left">When</th>
              <th className="text-left">Party</th>
              <th className="num">Last order</th>
              <th className="num">Avg gap</th>
              <th className="num">Days since</th>
              <th className="num">Predicted</th>
              <th className="num">Avg qty</th>
              <th className="num">Avg ₹</th>
              <th className="text-left">Likely items (last order)</th>
              <th className="text-right"></th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan="10" className="py-8 text-center text-sm text-gray-400">Crunching order history…</td></tr>}
            {!loading && rows.length === 0 && (
              <tr><td colSpan="10" className="py-8 text-center text-sm text-gray-400">
                <Sparkles size={28} className="inline-block text-gray-300 mb-1.5" />
                <div>No orders predicted in this window. All customers are on schedule.</div>
              </td></tr>
            )}
            {!loading && rows.map((r) => {
              const tag = tagFor(r);
              const wa = makeWhatsappLink(r);
              return (
                <tr key={r.party_id} data-testid={`prediction-row-${r.party_id}`}>
                  <td>
                    <span className={`px-2 py-0.5 text-xs rounded-sm font-semibold ${tag.cls}`}>{tag.txt}</span>
                  </td>
                  <td>
                    <Link to={`/parties/${r.party_id}`} className="font-semibold hover:underline">{r.party_name}</Link>
                    <div className="text-xs text-gray-500">
                      {r.phone || "—"} · {r.n_orders} past orders
                      {r.last_followup_date ? ` · last f/u ${r.last_followup_date}` : ""}
                    </div>
                  </td>
                  <td className="data-num text-xs">{r.last_order_date}</td>
                  <td className="num">{r.avg_gap_days}d</td>
                  <td className="num">{r.days_since_last}d</td>
                  <td className="data-num text-xs font-bold">{r.predicted_next_date}</td>
                  <td className="num">{r.avg_filled_qty}</td>
                  <td className="num">{formatMoney(r.avg_order_amount)}</td>
                  <td className="text-xs text-gray-700 max-w-[260px]">
                    {(r.last_items || []).slice(0, 3).map((it, i) => (
                      <span key={i} className="inline-block bg-gray-100 rounded-sm px-1.5 py-0.5 mr-1 mb-1">
                        {it.brand} {it.size}<span className="text-gray-500"> × {it.filled_qty}</span>
                      </span>
                    ))}
                    {(r.last_items || []).length === 0 && <span className="text-gray-400">—</span>}
                  </td>
                  <td className="text-right">
                    <div className="inline-flex items-center gap-2">
                      {r.phone && (
                        <a href={`tel:${r.phone}`} className="text-gray-500 hover:text-black" title="Call" data-testid={`pred-call-${r.party_id}`}>
                          <Phone size={14} />
                        </a>
                      )}
                      {wa && (
                        <a href={wa} target="_blank" rel="noreferrer" className="text-gray-500 hover:text-[#15803D]" title="WhatsApp reminder" data-testid={`pred-wa-${r.party_id}`}>
                          <MessageSquare size={14} />
                        </a>
                      )}
                      <button onClick={() => logFollowup(r)} className="btn-primary h-8 px-3 text-xs flex items-center gap-1.5" data-testid={`pred-log-${r.party_id}`}>
                        Log <ChevronRight size={13} />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <p className="text-xs text-gray-500 leading-relaxed">
        Predictions use the customer's own ordering pattern (more weight on the last 6 gaps). Customers with fewer than {minOrders} historical orders are hidden — they don't have enough data to estimate a reliable cycle.
      </p>
    </div>
  );
}
